<template>
  <div
    class="flex w-full justify-center items-center"
    style="min-height: calc(100vh - 2rem);">
    <div
      v-if="$apollo.queries.userInvitation.loading"
      class="py-10">
      <XSpinner class="text-5xl text-white m-auto" />
    </div>

    <XTransition v-if="!$apollo.queries.userInvitation.loading">
      <component
        :is="boxComponent"
        :class="$style['box']"
        :isAdmin="userInvitation.isAdmin"
        v-model="fields"
        :userName="fields.name"
        :confirmationCode="confirmationCode"
        :email="userInvitation.email"
        :errors="errors"
        :loading="loading"
        @next="nextAndPrevStep"
        @preview="nextAndPrevStep"
        @submit="onSubmit" />
    </XTransition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import gql from 'graphql-tag'

import XTransition from '@plat/components/Transition'

import XBasicInformationBox from './boxes/BasicInformationBox'
import XProcessorAgreementBox from './boxes/ProcessorAgreementBox'
import XErrorBox from './boxes/ErrorBox'
import XSuccessBox from './boxes/SuccessBox'

export default {
  components: {
    XTransition,
    XBasicInformationBox,
    XProcessorAgreementBox,
    XErrorBox,
    XSuccessBox
  },

  data () {
    return {
      fields: {
        name: '',
        lastName: '',
        company: '',
        password: '',
        termsConfirmed: false,
        privacyConfirmed: false,
        communicationsConfirmed: false,
        processorAgreementConfirmed: false
      },
      boxComponent: 'XBasicInformationBox',
      loading: false,
      errors: null
    }
  },

  computed: {
    ...mapState({
      accountId: state => ('account' in state.routeParameters) ? state.routeParameters.account.id : null,
      eventId: state => ('event' in state.routeParameters) ? state.routeParameters.event.id : null,
      confirmationCode: state => state.routeParameters.code
    })
  },

  apollo: {
    userInvitation: {
      query () {
        if (this.eventId !== null) {
          return gql`query Event ($eventId: Uuid!, $confirmationCode: String!) {
            event (eventId: $eventId) {
              userInvitation (confirmationCode: $confirmationCode) {
                email
                isAdmin
              }
            }
          }`
        } else {
          return gql`query Account ($accountId: Uuid!, $confirmationCode: String!) {
            account (accountId: $accountId) {
              userInvitation (confirmationCode: $confirmationCode) {
                email
                isAdmin
              }
            }
          }`
        }
      },
      variables () {
        return {
          accountId: this.accountId,
          eventId: this.eventId,
          confirmationCode: this.confirmationCode
        }
      },
      update (result) {
        if (this.eventId) {
          return result.hasOwnProperty('event') ? result.event.userInvitation : result
        } else {
          return result.hasOwnProperty('account') ? result.account.userInvitation : result
        }
      },
      error (error) {
        this.boxComponent = 'XErrorBox'

        this.errors = error.networkError
          ? error.networkError
          : error.graphQLErrors
      }
    }
  },

  methods: {
    onSubmit () {
      if (this.loading && !this.fields.termsConfirmed && !this.fields.processorAgreementConfirmed) {
        return
      }

      this.loading = true
      this.errors = null

      return this.signUp()
        .finally(() => {
          this.loading = false
        })
    },

    nextAndPrevStep () {
      this.boxComponent = this.boxComponent === 'XBasicInformationBox'
        ? 'XProcessorAgreementBox'
        : 'XBasicInformationBox'
    },

    signUp () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation confirmInvitation($confirmationCode: String!, $input: ConfirmInvitationInput!) {
              confirmInvitation(confirmationCode: $confirmationCode, input: $input)
            }
          `,
          variables: {
            confirmationCode: this.confirmationCode,
            input: this.fields
          }
        })
        .then(result => {
          if (result.data.confirmInvitation) {
            this.boxComponent = 'XSuccessBox'
          }
        })
        .catch(error => {
          this.boxComponent = 'XErrorBox'

          this.errors = error.networkError
            ? error.networkError
            : error.graphQLErrors
        })
    }
  }
}
</script>

<style lang="scss" module>
.box {
  width: 100%;
}

@media (min-width: 576px) {
  .box {
    width: 550px;
  }
}
</style>
